import React, { useMemo } from 'react';
import { ArrowRight, Inbox } from 'lucide-react';
import type { DemandaEsteira, EsteiraFila } from '../../../types';
import { ETAPAS_FLUXO, ETAPA_DA_FILA, FILAS, formatDia } from './filas';

interface Props {
    demandas: DemandaEsteira[];
    /** Clicar num card leva para a Lista, já filtrada naquela fila. */
    onAbrirFila: (fila: EsteiraFila) => void;
}

/** Até onde vai a coluna antes de virar "e mais N". Rolar 500 cards não é ver. */
const TETO_POR_COLUNA = 8;

/**
 * Modo Etapas — o fluxo, não as filas.
 *
 * As 16 filas são fatiadas por TIPO de documento; este quadro corta na
 * horizontal, por FASE do processo. "Aguardando assinatura" mora em cinco filas
 * diferentes (mora, advertência e três de distrato) e, separadas, ninguém vê que
 * é ali que o funil empoça.
 *
 * Não tem arrastar de propósito: cada fila é DERIVADA do dado de origem — um
 * documento sai de "aguardando assinatura" quando o Autentique confirma a
 * assinatura, não quando alguém arrasta um card. Arrastar daria a impressão de
 * que a tela manda no processo, e ela não manda.
 */
const QuadroEtapas: React.FC<Props> = ({ demandas, onAbrirFila }) => {
    const colunas = useMemo(() => {
        const porEtapa = new Map(ETAPAS_FLUXO.map(e => [e.etapa, [] as DemandaEsteira[]]));
        for (const d of demandas) {
            // Negativado é acompanhamento, não etapa de trabalho (#6209).
            if (d.status !== 'pendente' || d.acompanhamento) continue;
            porEtapa.get(ETAPA_DA_FILA[d.fila])?.push(d);
        }
        return ETAPAS_FLUXO.map(meta => {
            const itens = porEtapa.get(meta.etapa) ?? [];
            // Agrupa por fila dentro da coluna: é assim que "5 filas na mesma
            // etapa" fica visível em vez de virar uma lista indistinta.
            const porFila = new Map<EsteiraFila, number>();
            for (const d of itens) porFila.set(d.fila, (porFila.get(d.fila) ?? 0) + 1);
            return {
                meta,
                total: itens.length,
                paradas: itens.filter(d => d.dias_parado > 7).length,
                filas: [...porFila.entries()].sort((a, b) => b[1] - a[1]),
                amostra: itens.slice(0, TETO_POR_COLUNA),
            };
        });
    }, [demandas]);

    const maior = Math.max(1, ...colunas.map(c => c.total));

    return (
        <div className="space-y-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
                O mesmo trabalho, visto por <strong>fase do processo</strong> em vez de por tipo de documento —
                da esquerda para a direita, como ele anda. Clique numa fila para abrir a lista já filtrada.
            </p>

            {/* Uma faixa rolável: em tela estreita o quadro vira carrossel em vez
                de espremer seis colunas ilegíveis. `snap-x` faz o dedo parar
                coluna a coluna no celular em vez de deixar a rolagem solta —
                256px de seis colunas + setas passa de 1700px, e abaixo disso
                sempre vai rolar; o objetivo aqui é rolar bem, não caber inteiro. */}
            <div
                className="overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scroll-smooth focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-lg"
                tabIndex={0}
                role="group"
                aria-label="Etapas do processo, role horizontalmente com as setas do teclado"
            >
                <div className="flex gap-3 min-w-max">
                    {colunas.map(({ meta, total, paradas, filas, amostra }, i) => (
                        <div key={meta.etapa} className="flex items-start gap-3 snap-start">
                            <div className={`w-56 sm:w-60 shrink-0 rounded-lg border-t-4 ${meta.accent} bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                                <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{meta.titulo}</h3>
                                        <span className="text-xl font-black text-gray-900 dark:text-white">{total}</span>
                                    </div>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{meta.pergunta}</p>

                                    {/* Barra proporcional: mostra onde o funil empoça
                                        sem a pessoa precisar comparar números. */}
                                    <div className="mt-2 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                        <div
                                            className="h-full bg-primary/60 rounded-full"
                                            style={{ width: `${Math.round((total / maior) * 100)}%` }}
                                        />
                                    </div>
                                    {paradas > 0 ? (
                                        <p className="text-[11px] text-red-600 dark:text-red-400 font-semibold mt-1.5">
                                            {paradas} parada{paradas > 1 ? 's' : ''} há mais de 7 dias
                                        </p>
                                    ) : null}
                                </div>

                                {total === 0 ? (
                                    <div className="p-6 text-center text-gray-300 dark:text-gray-600">
                                        <Inbox className="w-6 h-6 mx-auto mb-1" />
                                        <p className="text-[11px]">nada aqui</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* As filas que compõem a etapa — o elo entre as
                                            duas leituras (fase × tipo de documento). */}
                                        <div className="p-3 space-y-1.5 border-b border-gray-100 dark:border-gray-700">
                                            {filas.map(([fila, n]) => (
                                                <button
                                                    key={fila}
                                                    onClick={() => onAbrirFila(fila)}
                                                    className="w-full flex items-center justify-between gap-2 text-left px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors group"
                                                >
                                                    <span className="text-[11px] text-gray-600 dark:text-gray-300 truncate group-hover:text-primary">
                                                        {FILAS[fila].titulo}
                                                    </span>
                                                    <span className={`text-[11px] font-bold px-1.5 rounded shrink-0 ${meta.chip}`}>{n}</span>
                                                </button>
                                            ))}
                                        </div>

                                        {/* Os mais antigos primeiro — a lista já vem ordenada
                                            por tempo parado, então a amostra é o topo real. */}
                                        <div className="p-3 space-y-2">
                                            {amostra.map(d => (
                                                <button
                                                    key={d.id}
                                                    onClick={() => onAbrirFila(d.fila)}
                                                    className="w-full text-left group"
                                                >
                                                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate group-hover:text-primary">
                                                        {d.cliente_nome || 'Cliente sem nome'}
                                                    </p>
                                                    <p className="text-[10px] text-gray-400 dark:text-gray-500">
                                                        {d.dias_parado <= 0 ? 'hoje' : `${d.dias_parado}d`}
                                                        {d.prazo_em ? ` · prazo ${formatDia(d.prazo_em)}` : ''}
                                                        {d.atribuicao?.responsavel_nome ? ` · ${d.atribuicao.responsavel_nome.split(' ')[0]}` : ''}
                                                    </p>
                                                </button>
                                            ))}
                                            {total > amostra.length ? (
                                                <p className="text-[11px] text-gray-400 dark:text-gray-500 pt-1">
                                                    e mais {total - amostra.length}…
                                                </p>
                                            ) : null}
                                        </div>
                                    </>
                                )}
                            </div>

                            {i < colunas.length - 1 ? (
                                <ArrowRight className="w-4 h-4 text-gray-300 dark:text-gray-600 mt-8 shrink-0" />
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default QuadroEtapas;
